import { useAppSelector } from 'app/hooks';
import { SystemOptionCondenserType, ValidSystemType } from 'data/types';
import { ComponentSwapHandler } from 'hooks/use-editable-system-map';
import { Fragment, useMemo, useState } from 'react';
import { KEPT_EQUIPMENT_PRODUCT_ID } from 'utils/equipment-placeholder';
import {
  getValidAirHandlerSwapProductsByZone,
  getValidCondenserSwapProducts
} from 'utils/system-swap-options';
import {
  BlowerRow,
  CondenserHeader,
  LockToggleButton,
  ZoneRow
} from './condenser-component-rows';
import SwapProductPopover from './swap-product-popover';

type CondenserComponentGroupProps = Readonly<{
  condenser: SystemOptionCondenserType;
  validSystems: ValidSystemType[];
  onComponentSwap: ComponentSwapHandler;
}>;

export default function CondenserComponentGroup({
  condenser,
  validSystems,
  onComponentSwap
}: CondenserComponentGroupProps) {
  const { condenserProducts, airHandlerProducts } = useAppSelector(
    (state) => state.data
  );
  const [condenserLocked, setCondenserLocked] = useState(true);
  const [lockedZones, setLockedZones] = useState<string[]>(() =>
    condenser.zones
      .filter((zone) => zone.selectedProduct != null)
      .map((zone) => zone.uniqueId)
  );

  const zones = condenser.zones.filter((zone) => zone.selectedProduct != null);

  const validCondenserProducts = useMemo(
    () =>
      getValidCondenserSwapProducts(
        validSystems,
        condenser,
        lockedZones,
        condenserProducts
      ),
    [validSystems, condenser, lockedZones, condenserProducts]
  );

  const validAirHandlerProductsByZone = useMemo(
    () =>
      getValidAirHandlerSwapProductsByZone(
        validSystems,
        condenser,
        condenserLocked,
        lockedZones,
        airHandlerProducts
      ),
    [validSystems, condenser, condenserLocked, lockedZones, airHandlerProducts]
  );

  const toggleZoneLock = (zoneId: string) => {
    setLockedZones((oldValues) =>
      oldValues.includes(zoneId)
        ? oldValues.filter((v) => v !== zoneId)
        : [...oldValues, zoneId]
    );
  };

  if (!condenser.selectedProduct) {
    return null;
  }

  const condenserKept =
    condenser.selectedProduct.productId === KEPT_EQUIPMENT_PRODUCT_ID;
  const locks = { condenserLocked, lockedZoneIds: lockedZones };

  return (
    <div className='overflow-hidden rounded-lg border border-gray-200'>
      <CondenserHeader
        condenser={condenser}
        zoneCount={zones.length}
        actions={
          <>
            {!condenserKept && (
              <SwapProductPopover
                variant='condenser'
                products={validCondenserProducts}
                currentProduct={condenser.selectedProduct}
                onSelect={(productId) =>
                  onComponentSwap('condenser', productId, undefined, locks)
                }
                hasActiveLocks={lockedZones.length > 0}
                tooltipText='Change Condenser'
              />
            )}
            <LockToggleButton
              locked={condenserLocked}
              subject='Condenser'
              onToggle={() => setCondenserLocked((oldValue) => !oldValue)}
            />
          </>
        }
      />
      {zones.map((zone) => {
        const zoneKept =
          zone.selectedProduct.productId === KEPT_EQUIPMENT_PRODUCT_ID;
        return (
          <Fragment key={zone.uniqueId}>
            <ZoneRow
              zone={zone}
              actions={
                <>
                  {!zoneKept && (
                    <SwapProductPopover
                      variant='airHandler'
                      products={
                        validAirHandlerProductsByZone[zone.uniqueId] ?? []
                      }
                      currentProduct={zone.selectedProduct}
                      onSelect={(productId) =>
                        onComponentSwap(
                          'airHandler',
                          productId,
                          zone.uniqueId,
                          locks
                        )
                      }
                      hasActiveLocks={condenserLocked || lockedZones.length > 0}
                      tooltipText='Change Air Handler'
                    />
                  )}
                  <LockToggleButton
                    locked={lockedZones.includes(zone.uniqueId)}
                    subject='Zone'
                    onToggle={() => toggleZoneLock(zone.uniqueId)}
                  />
                </>
              }
            />
            {zone.blowerProduct && <BlowerRow blower={zone.blowerProduct} />}
          </Fragment>
        );
      })}
    </div>
  );
}
